import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { isAxiosError } from 'axios';
import activityLogService, { type ActivityLog } from '../../services/activityLogService';
import authService from '../../services/authService';

type UserOption = { id: number; name: string; email?: string };

const formatDateTime = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
};

const ActivityLogsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 30;

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userId, setUserId] = useState<number | ''>('');
  const [filterNonce, setFilterNonce] = useState(0);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    authService
      .getAllUsers()
      .then((list: { id: number; name: string; email?: string }[]) => {
        if (!cancelled && Array.isArray(list)) setUsers(list.map((u) => ({ id: u.id, name: u.name, email: u.email })));
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    void filterNonce; // força novo fetch ao clicar "Aplicar" mesmo na página 1
    setLoading(true);
    setError(null);
    setEndpointMissing(false);
    try {
      const result = await activityLogService.fetchLogs({
        page,
        perPage,
        search: search.trim() || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        userId: userId === '' ? undefined : userId,
      });
      setLogs(result.logs);
      setLastPage(result.lastPage);
      setTotal(result.total);
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('ENDPOINT_MISSING')) {
        setEndpointMissing(true);
        setLogs([]);
        setTotal(0);
        setLastPage(1);
      } else if (isAxiosError(e)) {
        const msg =
          typeof e.response?.data?.message === 'string'
            ? e.response.data.message
            : `Erro ${e.response?.status ?? ''} ao carregar atividades`.trim();
        setError(msg || 'Não foi possível carregar o registro de atividades.');
      } else {
        setError('Não foi possível carregar o registro de atividades.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo, userId, filterNonce]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApplyFilters = () => {
    setPage(1);
    setFilterNonce((n) => n + 1);
  };

  const toggleMeta = (id: string | number) => {
    setExpanded((p) => ({ ...p, [String(id)]: !p[String(id)] }));
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <HistoryIcon color="primary" sx={{ fontSize: 36 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0 }}>
            Registro de atividades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulta o que cada usuário fez no sistema quando o servidor grava logs (requer backend).
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <span>
            <IconButton onClick={() => load()} disabled={loading} aria-label="Atualizar lista" color="primary">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {endpointMissing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          O frontend está configurado para chamar{' '}
          <strong>GET /api/activity-logs</strong>. Essa rota ainda não respondeu (404): é preciso expor no backend um
          endpoint paginado que grave e devolva as ações por usuário. Formato sugerido:{' '}
          <code>página com data[]</code> e opcionalmente <code>meta.current_page</code>, <code>meta.last_page</code>,{' '}
          <code>meta.total</code>, ou objeto <code>logs</code> com os mesmos campos aceitos pelo normalizador (Spatie
          Activity, <code>user</code>, <code>action</code>, <code>description</code>, etc.).
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Filtros
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr) auto' },
            gap: 2,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            label="Buscar (ação, texto…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="log-user-filter">Usuário</InputLabel>
            <Select<number | ''>
              labelId="log-user-filter"
              label="Usuário"
              value={userId}
              onChange={(e: SelectChangeEvent<number | ''>) => {
                const v = e.target.value as number | '';
                setUserId(v === '' ? '' : Number(v));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                  {u.email ? ` (${u.email})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="De (data)"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até (data)"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={handleApplyFilters} sx={{ height: 40 }}>
            Aplicar
          </Button>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {total ? `${total} registro${total !== 1 ? 's' : ''} encontrados` : endpointMissing ? '—' : 'Nenhum registro'}
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Data / hora</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ação</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Alvo</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 56 }}>
                Det.
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {endpointMissing ? 'Cadastre a API no backend para ver os registros aqui.' : 'Nenhuma atividade listada nesta página.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((row) => (
                <React.Fragment key={String(row.id)}>
                  <TableRow hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.userName ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.userEmail ?? (row.userId != null ? `ID ${row.userId}` : '')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.action} size="small" variant="outlined" />
                      {row.ipAddress ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          IP: {row.ipAddress}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2">{row.description ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.subjectType || row.subjectId != null ? (
                        <>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.subjectType ?? '—'}
                          </Typography>
                          <Typography variant="body2">{row.subjectId != null ? `#${row.subjectId}` : '—'}</Typography>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {row.metadata && Object.keys(row.metadata).length > 0 ? (
                        <Tooltip title={expanded[String(row.id)] ? 'Ocultar detalhes' : 'Ver detalhes'}>
                          <IconButton size="small" onClick={() => toggleMeta(row.id)}>
                            {expanded[String(row.id)] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span />
                      )}
                    </TableCell>
                  </TableRow>
                  {row.metadata && Object.keys(row.metadata).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded[String(row.id)] ? undefined : 'none', bgcolor: 'action.hover' }}>
                        <Collapse in={!!expanded[String(row.id)]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, fontFamily: 'monospace', fontSize: 12, overflow: 'auto', maxHeight: 220 }}>
                            <pre style={{ margin: 0 }}>
                              {(() => {
                                try {
                                  return JSON.stringify(row.metadata, null, 2);
                                } catch {
                                  return String(row.metadata);
                                }
                              })()}
                            </pre>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!endpointMissing && lastPage > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Pagination
            page={page}
            count={lastPage}
            onChange={(_, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default ActivityLogsPage;
